/**
 * Frota · Manutenção · OS do ERP · detalhe.
 *
 * Cabeçalho, serviços e peças da OS do Sinersoft, e o plano preventivo da placa:
 * os itens que o texto da OS indica vêm marcados; quem registra confirma, e a
 * execução entra no plano com a data e o KM da OS (sem duplicar).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceErpOrderDetailDto, MaintenanceErpOrderRegisterResultDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { ERP_STATUS, diaErp } from "./MaintenanceErpOrdersPage";
import { TableOrCards, useMobile } from "../features/maintenance-mobile/mobile-ui";

export function MaintenanceErpOrderPage() {
  const mobile = useMobile();
  const { id = "" } = useParams();
  const { can } = useCapabilities();
  const qc = useQueryClient();
  const os = useQuery({
    queryKey: ["maintenance", "erp-order", id],
    queryFn: () => api.get<MaintenanceErpOrderDetailDto>(`/maintenance/erp-orders/${id}`),
  });
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [resultado, setResultado] = useState<MaintenanceErpOrderRegisterResultDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (os.data) setMarcados(new Set(os.data.suggested.filter((c) => !os.data!.registered.includes(c))));
  }, [os.data]);

  const registrar = useMutation({
    mutationFn: () => api.post<MaintenanceErpOrderRegisterResultDto>(`/maintenance/erp-orders/${id}/plan-executions`, { componentCodes: [...marcados] }),
    onSuccess: (r) => {
      setResultado(r);
      setErro(null);
      qc.invalidateQueries({ queryKey: ["maintenance"] });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível registrar no plano."),
  });

  if (os.isLoading) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (!os.data) return <p className="text-sm text-danger-600">OS do ERP não encontrada.</p>;
  const o = os.data;
  const servicos = o.items.filter((i) => i.kind === "service");
  const pecas = o.items.filter((i) => i.kind === "part");
  const podeRegistrar = can("workorder.manage") && o.status !== "cancelada" && o.plan.length > 0;
  const alternar = (code: string) => setMarcados((s) => {
    const n = new Set(s);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={`OS nº ${o.number} do ERP`}
        subtitle={o.origin === "internal" ? "Oficina própria (Sinersoft · ordens de serviço)" : "Terceiro (Sinersoft · ordens de serviço externas)"}
        actions={mobile ? undefined : <Link className="btn-secondary" to="/frota/manutencao/erp">Voltar</Link>}
      />

      <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Campo rotulo="Placa">
          {o.plate ? <Link className="font-mono text-tm-600 hover:underline" to={`/frota/manutencao/preventiva?plate=${o.plate}`}>{o.plate}</Link> : "—"}
        </Campo>
        <Campo rotulo="Situação"><span className={ERP_STATUS[o.status].badge}>{ERP_STATUS[o.status].label}</span></Campo>
        <Campo rotulo="Abertura">{diaErp(o.openedAt)}</Campo>
        <Campo rotulo={o.origin === "internal" ? "Fechamento" : "Entrada da nota"}>{diaErp(o.origin === "internal" ? o.closedAt : o.entryDate)}</Campo>
        <Campo rotulo="KM">{o.odometer ? fmtNumber(o.odometer) : "—"}</Campo>
        <Campo rotulo={o.origin === "internal" ? "Responsável (motorista)" : "Fornecedor"}>
          {(o.origin === "internal" ? o.driverName : o.supplierName) ?? "—"}
        </Campo>
        <Campo rotulo="Peças / Mão de obra">{fmtBRL(o.parts)} / {fmtBRL(o.labor)}</Campo>
        <Campo rotulo="Total"><span className="font-semibold">{fmtBRL(o.total)}</span></Campo>
      </div>

      <Itens titulo="Serviços" itens={servicos} />
      <Itens titulo="Peças" itens={pecas} />

      {o.notes && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Observações</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{o.notes}</p>
          <p className="text-xs text-gray-400 mt-2">Dados de pagamento (PIX, CPF/CNPJ, conta) ficam ocultos.</p>
        </div>
      )}

      <div className="card p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Registrar no plano preventivo</h3>
          <p className="text-xs text-gray-500">
            {o.plan.length
              ? `Marque o que esta OS executou. Entra no plano de ${o.plate} com a data e o KM da OS. Os itens que o texto indica já vêm marcados.`
              : "Esta placa não tem plano preventivo (fora da frota própria ativa ou sem placa)."}
          </p>
        </div>
        {o.plan.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-1">
            {o.plan.map((p) => {
              const feito = o.registered.includes(p.componentCode);
              return (
                <li key={p.componentCode}>
                  <label className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${feito ? "text-gray-400" : "text-gray-700"}`}>
                    <input type="checkbox" disabled={feito || !podeRegistrar} checked={feito || marcados.has(p.componentCode)} onChange={() => alternar(p.componentCode)} />
                    <span>{p.label}</span>
                    {feito && <span className="badge-green">registrado</span>}
                    {!feito && o.suggested.includes(p.componentCode) && <span className="badge-blue">sugerido</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {podeRegistrar && (
          <button className="btn-primary" disabled={!marcados.size || registrar.isPending} onClick={() => registrar.mutate()}>
            {registrar.isPending ? "Registrando…" : `Registrar ${marcados.size || ""} no plano`}
          </button>
        )}
        {erro && <p className="text-sm text-danger-600">{erro}</p>}
        {resultado && (
          <ul className="text-sm space-y-1">
            {resultado.registered.map((r) => (
              <li key={r.componentCode} className={r.ok ? "text-emerald-700" : "text-danger-600"}>
                {o.plan.find((p) => p.componentCode === r.componentCode)?.label ?? r.componentCode}: {r.ok ? r.message ?? "registrado" : r.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{rotulo}</p>
      <div className="text-gray-900">{children}</div>
    </div>
  );
}

function Itens({ titulo, itens }: { titulo: string; itens: MaintenanceErpOrderDetailDto["items"] }) {
  return (
    <div className="card overflow-x-auto">
      <h3 className="text-sm font-semibold text-gray-900 px-4 pt-3">{titulo}</h3>
      {itens.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">Nenhum item no ERP.</p>
      ) : (
        <TableOrCards
          cards={
            <ul className="mt-2 divide-y divide-surface-border border-t border-surface-border">
              {itens.map((i) => (
                <li key={`${i.kind}-${i.sequence}`} className="px-4 py-2.5">
                  <p className="text-sm text-gray-900">{i.description}</p>
                  <p className="mt-0.5 flex justify-between text-xs text-gray-500">
                    <span>{fmtNumber(i.quantity)} × {i.unitValue == null ? "—" : fmtBRL(i.unitValue)}</span>
                    <span className="text-sm font-semibold text-gray-900">{i.totalValue == null ? "—" : fmtBRL(i.totalValue)}</span>
                  </p>
                </li>
              ))}
            </ul>
          }
          table={
        <table className="w-full text-sm mt-2">
          <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2">Descrição</th>
              <th className="text-right px-4 py-2">Qtd.</th>
              <th className="text-right px-4 py-2">Unitário</th>
              <th className="text-right px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {itens.map((i) => (
              <tr key={`${i.kind}-${i.sequence}`}>
                <td className="px-4 py-2 text-gray-900">{i.description}</td>
                <td className="px-4 py-2 text-right text-gray-600">{fmtNumber(i.quantity)}</td>
                <td className="px-4 py-2 text-right text-gray-600">{i.unitValue == null ? "—" : fmtBRL(i.unitValue)}</td>
                <td className="px-4 py-2 text-right text-gray-900">{i.totalValue == null ? "—" : fmtBRL(i.totalValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
          }
        />
      )}
    </div>
  );
}
